import type { WeekData, TransactionDetail } from '../types/planner';
import type { RecurringItem } from '../types/recurring';
import type { CategoryType } from '../types/category';
import { getCategoryType } from './categoryMapper';
import { parseDate, isDateInWeek, formatDate } from './dateUtils';
import type { Category } from '../types/category';
import type { BudgetExpectedTransaction } from './budgetUtils';
import type { Asset } from '../types/asset';

const ADJUSTMENTS_STORAGE_KEY = 'planner_adjustments_detail';

interface Adjustment {
  id: string;
  date: string;
  categoryId: number | null;
  amount: number;
  sign?: '+' | '-'; // Optional for backward compatibility
  note?: string; // Optional note field
}

interface RecurringItemWithType extends RecurringItem {
  categoryType: CategoryType;
}

/**
 * Load adjustments from localStorage
 */
function loadAdjustments(): Adjustment[] {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem(ADJUSTMENTS_STORAGE_KEY);
  if (!stored) return [];
  
  const parsed = JSON.parse(stored);
  // Migrate old adjustments without sign field - default to '-'
  // Ensure amount is always positive and sign is always '+' or '-'
  return parsed.map((adj: any) => ({
    ...adj,
    amount: Math.abs(adj.amount || 0), // Always store as positive
    sign: (adj.sign === '+' || adj.sign === '-') ? adj.sign : '-', // Ensure valid sign
  }));
}

export interface CalculationOptions {
  useBudgets?: boolean;
  useRecurring?: boolean;
  budgetOverridesRecurring?: boolean; // If true, budgets override recurring; if false, recurring overrides budgets
}

/** YYYY-MM-DD prefix for API date strings. */
function dateKeyOnly(isoOrDate: string): string {
  return isoOrDate.length >= 10 ? isoOrDate.slice(0, 10) : isoOrDate;
}

/** Cleared activity: matched txn date or occurrence key is on/before today (in account balances). */
function occurrenceClearedInBalances(item: RecurringItem, dateStr: string): boolean {
  const summaries = item.occurrences[dateStr];
  if (!Array.isArray(summaries) || summaries.length === 0) return false;

  const todayStr = formatDate(new Date());

  return summaries.some((s) => {
    const summaryDate = dateKeyOnly(s.date);
    return summaryDate <= todayStr || dateStr <= todayStr;
  });
}

/**
 * Calculate planner data for all weeks
 */
export function calculatePlannerData(
  weeks: Array<{ start: Date; end: Date; date: string }>,
  recurringItems: RecurringItem[],
  categories: Category[],
  startingBalance: number = 0,
  adjustments: Record<string, number> = {}, // Keep for backward compatibility, but will be ignored
  bufferValue: number = 2000,
  budgetExpectedTransactions: BudgetExpectedTransaction[] = [],
  options: CalculationOptions = {
    useBudgets: false,
    useRecurring: true,
    budgetOverridesRecurring: true,
  },
  assets: Asset[] = []
): WeekData[] {
  // Load adjustments from the Adjustments page
  const detailAdjustments = loadAdjustments();
  // Create a map of category ID to category type
  const categoryTypeMap = new Map<number, CategoryType>();
  categories.forEach(cat => {
    categoryTypeMap.set(cat.id, getCategoryType(cat));
  });
  // Create a map of category ID to category name
  const categoryNameMap = new Map<number, string>();
  categories.forEach(cat => {
    categoryNameMap.set(cat.id, cat.name);
  });
  // Create a map of asset ID to asset name (use display_name if available, otherwise name)
  const assetNameMap = new Map<number, string>();
  assets.forEach(asset => {
    assetNameMap.set(asset.id, asset.display_name || asset.name);
  });

  // Enrich recurring items with category types
  const itemsWithTypes: RecurringItemWithType[] = recurringItems.map(item => ({
    ...item,
    categoryType: item.category_id ? (categoryTypeMap.get(item.category_id) || null) : null,
  }));

  const weekData: WeekData[] = [];
  let runningBudgetedNet = 0;
  let runningAdjustment = 0;
  let runningBufferMin = Infinity;
  let runningMin = Infinity;
  let previousBalance = startingBalance; // Track previous week's balance


  weeks.forEach((week) => {
    // Calculate totals for this week
    let income = 0;
    let debt = 0;
    let nonDiscretionary = 0;
    let discretionary = 0;

    // Track transaction details for hover popups
    const incomeDetails: TransactionDetail[] = [];
    const debtDetails: TransactionDetail[] = [];
    const nonDiscretionaryDetails: TransactionDetail[] = [];
    const discretionaryDetails: TransactionDetail[] = [];
    const adjustmentDetails: TransactionDetail[] = [];

    // Track which categories have been processed from budgets or recurring
    // Used for preference ordering (budget overrides recurring or vice versa)
    const processedCategories = new Set<number>();

    // When budgetOverridesRecurring is false, process recurring first so it can override budgets
    // When budgetOverridesRecurring is true, process budgets first so they can override recurring
    const processRecurringFirst = !options.budgetOverridesRecurring;

    if (processRecurringFirst && options.useRecurring) {
      // Process recurring items first (when they override budgets)
      itemsWithTypes.forEach(item => {
        Object.keys(item.occurrences).forEach(dateStr => {
          const occurrenceDate = parseDate(dateStr);
          if (isDateInWeek(occurrenceDate, week.start)) {
            const categoryId = item.category_id;
            const amount = parseFloat(item.amount);
            const type = item.categoryType;
            const categoryName = categoryId ? (categoryNameMap.get(categoryId) || 'Unknown') : 'Uncategorized';
            const accountName = item.asset_id ? (assetNameMap.get(item.asset_id) || `Account ${item.asset_id}`) : 'N/A';
            const alreadyAccountedInBalance = occurrenceClearedInBalances(item, dateStr);
            const detail: TransactionDetail = {
              payee: item.payee || 'N/A',
              category: categoryName,
              account: accountName,
              amount: Math.abs(amount),
              source: 'recurring',
              alreadyAccountedInBalance,
            };

            const countTowardWeek = !alreadyAccountedInBalance;

            switch (type) {
              case 'Income':
                if (countTowardWeek) income += Math.abs(amount);
                incomeDetails.push(detail);
                break;
              case 'Debt':
                if (countTowardWeek) debt += Math.abs(amount);
                debtDetails.push(detail);
                break;
              case 'Non-Discretionary':
                if (countTowardWeek) nonDiscretionary += Math.abs(amount);
                nonDiscretionaryDetails.push(detail);
                break;
              case 'Discretionary':
                if (countTowardWeek) discretionary += Math.abs(amount);
                discretionaryDetails.push(detail);
                break;
            }
            
            if (categoryId) {
              processedCategories.add(categoryId);
            }
          }
        });
      });
    }

    // Process budget expected transactions if enabled
    if (options.useBudgets) {
      budgetExpectedTransactions.forEach(budgetTx => {
        if (budgetTx.date === week.date) {
          const categoryId = budgetTx.category_id;
          
          // Check if we should use this budget transaction
          // If budgetOverridesRecurring is true, always use budgets (they override recurring)
          // If false, only use if recurring hasn't already processed this category (recurring overrides budgets)
          const shouldUse = options.budgetOverridesRecurring 
            ? true  // Budgets override recurring, so always use budgets
            : !processedCategories.has(categoryId || -1);  // Only use if recurring hasn't processed it yet
          
          if (shouldUse) {
            const type = budgetTx.categoryType;
            const amount = Math.abs(budgetTx.amount);
            const categoryName = categoryId ? (categoryNameMap.get(categoryId) || 'Unknown') : 'Uncategorized';
            const detail: TransactionDetail = {
              payee: budgetTx.payee || 'N/A',
              category: categoryName,
              account: 'N/A', // Budgets don't have account info
              amount: amount,
              source: 'budget',
            };

            if (type === 'Income') {
              income += amount;
              incomeDetails.push(detail);
            } else if (type === 'Debt') {
              debt += amount;
              debtDetails.push(detail);
            } else if (type === 'Non-Discretionary') {
              nonDiscretionary += amount;
              nonDiscretionaryDetails.push(detail);
            } else if (type === 'Discretionary') {
              discretionary += amount;
              discretionaryDetails.push(detail);
            } else {
              // If category type is null or unknown, default to Non-Discretionary
              // This ensures budget transactions are still included even if category mapping is missing
              nonDiscretionary += amount;
              nonDiscretionaryDetails.push(detail);
            }
            
            if (categoryId) {
              processedCategories.add(categoryId);
            }
          }
        }
      });
    } else {
      if (budgetExpectedTransactions.length > 0) {
        console.warn(`[Budget] Budget transactions exist (${budgetExpectedTransactions.length}) but useBudgets is false`);
      }
    }

    // Process recurring items if enabled (when budgets override recurring)
    if (!processRecurringFirst && options.useRecurring) {
      itemsWithTypes.forEach(item => {
        // Check all occurrence dates for this item
        Object.keys(item.occurrences).forEach(dateStr => {
          const occurrenceDate = parseDate(dateStr);
          if (isDateInWeek(occurrenceDate, week.start)) {
            const categoryId = item.category_id;
            
            // Only use if budgets haven't already processed this category (budgets override recurring)
            const shouldUse = !processedCategories.has(categoryId || -1);
            
            if (shouldUse) {
              const amount = parseFloat(item.amount);
              const type = item.categoryType;
              const categoryName = categoryId ? (categoryNameMap.get(categoryId) || 'Unknown') : 'Uncategorized';
              const accountName = item.asset_id ? (assetNameMap.get(item.asset_id) || `Account ${item.asset_id}`) : 'N/A';
              const alreadyAccountedInBalance = occurrenceClearedInBalances(item, dateStr);
              const detail: TransactionDetail = {
                payee: item.payee || 'N/A',
                category: categoryName,
                account: accountName,
                amount: Math.abs(amount),
                source: 'recurring',
                alreadyAccountedInBalance,
              };

              const countTowardWeek = !alreadyAccountedInBalance;

              switch (type) {
                case 'Income':
                  // Income amounts are typically negative in API (outflow), make positive
                  if (countTowardWeek) income += Math.abs(amount);
                  incomeDetails.push(detail);
                  break;
                case 'Debt':
                  // Debt payments are positive in API
                  if (countTowardWeek) debt += Math.abs(amount);
                  debtDetails.push(detail);
                  break;
                case 'Non-Discretionary':
                  // Expenses are positive in API
                  if (countTowardWeek) nonDiscretionary += Math.abs(amount);
                  nonDiscretionaryDetails.push(detail);
                  break;
                case 'Discretionary':
                  // Expenses are positive in API
                  if (countTowardWeek) discretionary += Math.abs(amount);
                  discretionaryDetails.push(detail);
                  break;
              }
              
              if (categoryId) {
                processedCategories.add(categoryId);
              }
            }
          }
        });
      });
    }

    const expense = nonDiscretionary + discretionary + debt;
    const budgeted = income - expense;
    
    runningBudgetedNet += budgeted;
    
    // Sum all adjustments that fall within this week's date range
    // Apply sign: '+' adds to budget (positive), '-' subtracts from budget (negative)
    let adjustment = 0;
    detailAdjustments.forEach(adj => {
      const adjDate = parseDate(adj.date);
      // Check if adjustment date falls within this week (inclusive of start and end)
      if (isDateInWeek(adjDate, week.start)) {
        // Get the sign from the adjustment (from the +/- toggle)
        // The sign field comes directly from the toggle button state
        const toggleSign = adj.sign;
        
        // Ensure we have a valid sign, default to '-' if missing or invalid
        const validSign: '+' | '-' = (toggleSign === '+' || toggleSign === '-') ? toggleSign : '-';
        
        // Amount should always be stored as positive - the sign toggle determines direction
        const amount = Math.abs(adj.amount || 0);
        
        // Apply the sign from the toggle: '+' adds (positive), '-' subtracts (negative)
        // This is the critical part: if toggle shows '-', we make the amount negative
        const signedAmount = validSign === '+' ? amount : -amount;
        
        adjustment += signedAmount;
        
        // Track adjustment details for hover popup
        const categoryName = adj.categoryId ? (categoryNameMap.get(adj.categoryId) || 'Unknown') : 'Uncategorized';
        adjustmentDetails.push({
          payee: 'N/A', // Adjustments don't have payee
          category: categoryName,
          account: 'N/A', // Adjustments don't have account
          amount: signedAmount, // Keep the signed amount for display
          source: 'adjustment',
          note: adj.note,
        });
      }
    });
    
    // Also include legacy adjustments for backward compatibility
    adjustment += adjustments[week.date] || 0;
    
    // Accumulate adjustments cumulatively (like budgetedNet)
    runningAdjustment += adjustment;
    
    // Adjusted Net = Budgeted + Adjustment (weekly, not cumulative)
    const adjustedNet = budgeted + adjustment;
    // Balance = Last week Balance + Adjusted Net
    const balance = previousBalance + adjustedNet;
    const balanceBuffer = balance - bufferValue; // Configurable buffer
    
    // Update previousBalance for next iteration
    previousBalance = balance;

    // Update running minimums
    runningBufferMin = Math.min(runningBufferMin, balanceBuffer);
    runningMin = Math.min(runningMin, balance);

    weekData.push({
      weekStart: week.start,
      weekEnd: week.end,
      date: week.date,
      income,
      debt,
      nonDiscretionary,
      discretionary,
      expense,
      budgeted,
      budgetedNet: runningBudgetedNet,
      adjustment,
      adjustedNet,
      balance,
      balanceBuffer,
      bufferMin: runningBufferMin,
      min: runningMin,
      incomeDetails,
      debtDetails,
      nonDiscretionaryDetails,
      discretionaryDetails,
      adjustmentDetails,
    });
  });

  // Summary of budget transactions
  if (options.useBudgets) {
    const totalBudgetDetails = weekData.reduce((sum, week) => {
      return sum + 
        week.incomeDetails.filter(d => d.source === 'budget').length +
        week.debtDetails.filter(d => d.source === 'budget').length +
        week.nonDiscretionaryDetails.filter(d => d.source === 'budget').length +
        week.discretionaryDetails.filter(d => d.source === 'budget').length;
    }, 0);
    console.log(`[Budget] Added ${totalBudgetDetails} budget transactions to planner across ${weekData.length} weeks`);
    if (totalBudgetDetails === 0 && budgetExpectedTransactions.length > 0) {
      console.error(`[Budget] ERROR: ${budgetExpectedTransactions.length} budget transactions were created but NONE were added to detail arrays`);
    }
  }

  // Summary of recurring transactions
  if (options.useRecurring) {
    const totalRecurringDetails = weekData.reduce((sum, week) => {
      return sum + 
        week.incomeDetails.filter(d => d.source === 'recurring').length +
        week.debtDetails.filter(d => d.source === 'recurring').length +
        week.nonDiscretionaryDetails.filter(d => d.source === 'recurring').length +
        week.discretionaryDetails.filter(d => d.source === 'recurring').length;
    }, 0);
    
    // Calculate totals by type (amounts counted toward weekly projection only; strikethrough rows omitted)
    const recurringIncome = weekData.reduce((sum, week) => {
      return sum + week.incomeDetails
        .filter(d => d.source === 'recurring' && !d.alreadyAccountedInBalance)
        .reduce((weekSum, d) => weekSum + d.amount, 0);
    }, 0);
    
    const recurringExpenses = weekData.reduce((sum, week) => {
      return sum + 
        week.debtDetails.filter(d => d.source === 'recurring' && !d.alreadyAccountedInBalance).reduce((weekSum, d) => weekSum + d.amount, 0) +
        week.nonDiscretionaryDetails.filter(d => d.source === 'recurring' && !d.alreadyAccountedInBalance).reduce((weekSum, d) => weekSum + d.amount, 0) +
        week.discretionaryDetails.filter(d => d.source === 'recurring' && !d.alreadyAccountedInBalance).reduce((weekSum, d) => weekSum + d.amount, 0);
    }, 0);
    
    console.log(`[Recurring] Added ${totalRecurringDetails} recurring transactions to planner across ${weekData.length} weeks`);
    console.log(`[Recurring] Total recurring income (toward weeks): $${recurringIncome.toFixed(2)}, Total recurring expenses (toward weeks): $${recurringExpenses.toFixed(2)}`);
    
    if (totalRecurringDetails === 0 && recurringItems.length > 0) {
      console.warn(`[Recurring] WARNING: ${recurringItems.length} recurring items were found but NONE were added to detail arrays`);
    }
  }

  return weekData;
}
